// Apolo IDE — CodeComposer (home, sprint composer-anchor E4).
//
// Composer da home ("Criar sessão"). Redesenho:
//   - GitContextHeader no topo do card = âncora de alvo autoritativa (git vivo,
//     não a pref salva).
//   - GuardBanner pré-envio quando o repo selecionado conflita com a pasta:
//     bloqueia o envio até resolver (poka-yoke).
//   - Toolbar unificada: Mode segmentado + Scope/Repo/Branch (absorve o antigo
//     CodeFooter) + utilitários + modelo UMA vez só + Skills + Enviar.
//   - Placeholder contextual ecoa o alvo.
//   - ConfirmDialog cirúrgico pra Direto em branch protegida.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Command, Folder, GitBranch, Paperclip, Send, ShieldCheck, ShieldQuestion, Sparkles } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { useComposerModels } from '../../hooks/useComposerModels.js';
import { useGitContext } from '../../hooks/useGitContext.js';
import { evaluateSend } from '../../lib/composerGuard.js';
import SlashCommandAutocomplete from './SlashCommandAutocomplete.jsx';
import { removeSlashToken } from '../../hooks/useSlashCommands.js';
import InvokedSkillsBar from './InvokedSkillsBar.jsx';
import ModelDropdown from './ModelDropdown.jsx';
import PillDropdown from './PillDropdown.jsx';
import RepoPickerPill from './RepoPickerPill.jsx';
import SegmentedControl from './SegmentedControl.jsx';
import GitContextHeader from './GitContextHeader.jsx';
import GuardBanner from './GuardBanner.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import MicButton from '../MicButton.jsx';
import AttachmentChips from './AttachmentChips.jsx';
import AttachmentDropOverlay from './AttachmentDropOverlay.jsx';
import SkillTemplatesModal from './SkillTemplatesModal.jsx';
import TeamModeToggle from './team/TeamModeToggle.jsx';
import {
  useFileAttachments,
  serializeAttachmentsWithContent,
} from '../../hooks/useFileAttachments.js';
import { fireHooks } from '../../lib/hooksRunner.js';
import { sessionKind } from '../../lib/code/sessionKind.js';
import { clearDraft, readDraft, writeDraft, listDrafts } from '../../lib/composer/draftStore.js';
import { loadAttachmentDraft, saveAttachmentDraft } from '../../lib/composer/attachmentDrafts.js';
import ComposerSubmitError from './ComposerSubmitError.jsx';
import { createClientIntentId } from '../../lib/composer/newChatIntent.js';

const SCOPE_OPTIONS = [
  { value: 'local', label: 'Local' },
  { value: 'pedros-agents', label: 'Pedros Agents' },
  { value: 'github', label: 'GitHub' },
];
const MODE_OPTIONS = [
  { value: 'worktree', label: 'Worktree', title: 'Isolado num worktree próprio — não toca sua árvore viva' },
  { value: 'direct', label: 'Direto', title: 'Escreve direto na árvore de trabalho viva' },
];
const BRANCH_OPTIONS = [
  { value: 'main', label: 'main' },
  { value: 'develop', label: 'develop' },
];

import { useAuthStore } from '../../store/useAuthStore.js';

export default function CodeComposer({ onSubmit, placeholder, primaryAction = 'Enviar', newChatRequest, onNewChatHandled }) {
  const account = useAuthStore((s) => s.user?.id || 'anonymous');
  const profile = useAuthStore((s) => s.activeProfileId || 'default');
  const draftScope = `${account}:${profile}:code`;
  const [initialDraft] = useState(() => readDraft(typeof window !== 'undefined' ? window.localStorage : null, draftScope));
  const [text, setText] = useState(() => initialDraft?.text || '');
  const [clientIntentId, setClientIntentId] = useState(() => initialDraft?.client_intent_id || createClientIntentId());
  // Skills invocadas via `/` viram PILLS (não ficam como /texto cru); reinjetadas
  // no prompt no envio. Array de comandos { slash_command, ... }.
  const [invokedSkills, setInvokedSkills] = useState(() => initialDraft?.skills || []);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Override one-shot: "Usar o repo selecionado mesmo assim" no banner de conflito.
  const [conflictAck, setConflictAck] = useState(false);
  // Pasta local escolhida (scope=local). Vence o repo.detect (que só conhece o
  // ~/Apolo) tanto pra ÂNCORA (root do useGitContext) quanto pra CRIAÇÃO da
  // sessão (directory no onSubmit). Bug do Pedro: sem isso, scope Local sempre
  // alvejava ~/Apolo. Espelha o seletor do NewSessionModal.
  const [localDir, setLocalDir] = useState(() => initialDraft?.directory || '');
  const textareaRef = useRef(null);
  const ui = useAgentStore((s) => s.code.ui);
  const setPref = useAgentStore((s) => s.setUiPref);
  const { groups: modelGroups } = useComposerModels();

  useEffect(() => {
    if (!initialDraft) return;
    for (const key of ['model', 'mode', 'scope', 'github_repo', 'permissions_mode']) { if (initialDraft[key] != null) setPref(key, initialDraft[key]); }
  }, [initialDraft, setPref]);

  useEffect(() => {
    writeDraft(typeof window !== 'undefined' ? window.localStorage : null, {
      text,
      skills: invokedSkills,
      mode: ui.mode,
      permissions_mode: ui.permissions_mode,
      model: ui.model,
      scope: ui.scope,
      directory: localDir,
      github_repo: ui.github_repo,
      client_intent_id: clientIntentId,
    }, draftScope);
  }, [text, ui.mode, ui.permissions_mode, ui.model, ui.scope, ui.github_repo, localDir, clientIntentId, draftScope, invokedSkills]);

  useEffect(() => {
    if (ui.workbenchSurface !== 'code') return;
    useAgentStore.setState((state) => ({ code: { ...state.code, newChatContext: { scope: `${account}:${profile}`, directory: ui.scope === 'local' ? localDir || null : null, github_repo: ui.scope === 'github' ? ui.github_repo : null } } }));
  }, [account, profile, localDir, ui.scope, ui.github_repo, ui.workbenchSurface]);

  const selectedRepo = ui.scope === 'github' ? ui.github_repo || null : null;
  const { context } = useGitContext({
    root: ui.scope === 'local' ? (localDir || null) : null,
    selectedRepo,
    // GitHub scope ⇒ the target is the SELECTED repo (cloned); don't probe the app's
    // own folder (~/Apolo) and flag a phantom conflict with the picked repo.
    detectLocal: ui.scope !== 'github',
  });
  const verdict = useMemo(
    () => evaluateSend({ context, scope: ui.scope, mode: ui.mode }),
    [context, ui.scope, ui.mode],
  );
  const blocked = !verdict.canSend && !conflictAck;

  // (E5) provenance — Local/Cloud indicator next to the model dropdown, derived
  // from the selected model. Icon null = modelo não mapeado (silente), igual à
  // strip do composer in-session.
  const modelKind = sessionKind(ui.model);

  // Resetar o ack quando o conflito some (ou a pref muda).
  useEffect(() => {
    if (context?.health !== 'conflict') setConflictAck(false);
  }, [context?.health, selectedRepo]);

  const {
    attachments,
    draftReady,
    dragOver,
    uploadError,
    fileInputRef,
    removeAttachment,
    clear: clearAttachments,
    restoreAttachments,
    openFilePicker,
    dropZoneHandlers,
    fileInputHandlers,
  } = useFileAttachments({ draftKey: draftScope });

  function handlePickCommand(cmd, range) {
    // A skill vira PILL: remove o /token ativo do textarea + acumula em invokedSkills.
    // `range` (caret-aware) remove EXATAMENTE o token e PRESERVA o texto DEPOIS do
    // cursor → invocar a skill no MEIO da frase funciona. Sem range → fallback.
    const { text: nextText, caret: nextCaret } = removeSlashToken(text, range);
    setText(nextText);
    setInvokedSkills((prev) =>
      prev.some((c) => c.slash_command === cmd.slash_command) ? prev : [...prev, cmd],
    );
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      if (typeof nextCaret === 'number') el.setSelectionRange(nextCaret, nextCaret);
    });
  }

  const submittingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [savedDrafts, setSavedDrafts] = useState(() => listDrafts(window.localStorage, draftScope));

  const archiveCurrent = useCallback(async () => {
    if (!text.trim() && !attachments.length && !invokedSkills.length) return;
    if (attachments.length) await saveAttachmentDraft(`intent:${clientIntentId}`, attachments);
    const saved = writeDraft(window.localStorage, {
      text, skills: invokedSkills, mode: ui.mode, model: ui.model,
      permissions_mode: ui.permissions_mode, scope: ui.scope,
      directory: localDir, github_repo: ui.github_repo, client_intent_id: clientIntentId,
    }, `${draftScope}:archive:${clientIntentId}`);
    if (!saved) throw new Error('Não foi possível salvar o rascunho atual.');
    setSavedDrafts(listDrafts(window.localStorage, draftScope));
  }, [text, attachments, invokedSkills, clientIntentId, ui, localDir, draftScope]);

  async function restoreSaved(id) {
    if (submittingRef.current || !draftReady || !id) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      await archiveCurrent();
      const draft = readDraft(window.localStorage, `${draftScope}:archive:${id}`);
      if (!draft) return;
      const files = await loadAttachmentDraft(`intent:${id}`);
      setText(draft.text); setClientIntentId(id); setInvokedSkills(draft.skills || []);
      setLocalDir(draft.directory || '');
      for (const key of ['model', 'mode', 'scope', 'github_repo', 'permissions_mode']) {
        if (draft[key] != null) setPref(key, draft[key]);
      }
      restoreAttachments(files);
      setSubmitError(null);
    } catch (error) { setSubmitError({ error: error.message }); }
    finally { submittingRef.current = false; setBusy(false); }
  }

  const handledRequest = useRef(null);
  useEffect(() => {
    if (!newChatRequest || handledRequest.current === newChatRequest.id || !draftReady || busy || submittingRef.current) return;
    handledRequest.current = newChatRequest.id;
    submittingRef.current = true;
    setBusy(true);
    archiveCurrent().then(() => {
      setText(''); setInvokedSkills([]); setClientIntentId(createClientIntentId());
      setSubmitError(null); setConflictAck(false); setConfirmOpen(false);
      setLocalDir(newChatRequest.directory || '');
      setPref('scope', newChatRequest.github_repo ? 'github' : 'local');
      setPref('github_repo', newChatRequest.github_repo || null);
      clearAttachments();
      requestAnimationFrame(() => textareaRef.current?.focus());
    }).catch((error) => setSubmitError({ error: error.message })).finally(() => {
      submittingRef.current = false; setBusy(false);
      onNewChatHandled?.(newChatRequest.id);
    });
  }, [newChatRequest, draftReady, busy, archiveCurrent, setPref, clearAttachments, onNewChatHandled]);

  async function doSubmit() {
    if (!draftReady || submittingRef.current) return;
    const value = text.trim();
    if (!value && attachments.length === 0 && invokedSkills.length === 0) return;
    submittingRef.current = true;
    setBusy(true);
    setSubmitError(null);
    try {
    // Skills invocadas (pills) reinjetadas na FRENTE do prompt pro agente.
    const skillPrefix = invokedSkills.map((c) => c.slash_command).join(' ');
    const withSkills = skillPrefix ? `${skillPrefix} ${value}` : value;
    const effectiveText = serializeAttachmentsWithContent(withSkills, attachments);

    const hookResult = await fireHooks('user_prompt_submit', {
      tool: 'user_prompt_submit',
      prompt: effectiveText.slice(0, 4000),
      model: ui.model || '',
      permissions_mode: ui.permissions_mode || '',
    });
    if (hookResult.blocked) {
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'error', message: `Hook bloqueou: ${hookResult.blockingStderr?.slice(0, 200) || 'sem motivo'}` },
      }));
      return;
    }

    if (attachments.length) await saveAttachmentDraft(`intent:${clientIntentId}`, attachments);
    const result = await onSubmit?.({
      text: effectiveText,
      model: ui.model,
      permissions_mode: ui.permissions_mode,
      attachments,
      github_repo: ui.scope === 'github' ? ui.github_repo || null : null,
      // Send the same local target that the authoritative header displays.
      directory: ui.scope === 'local' ? localDir || context?.dir || undefined : undefined,
      client_intent_id: clientIntentId,
    }, draftScope);
    if (result?.ok === false) { setSubmitError(result); return; }
    setText('');
    clearDraft(typeof window !== 'undefined' ? window.localStorage : null, draftScope);
    setClientIntentId(createClientIntentId());
    clearAttachments();
    setInvokedSkills([]);
    setConflictAck(false);
    } catch (error) {
      setSubmitError({ error: error.message || 'Não foi possível enviar. Rascunho preservado.' });
    } finally { submittingRef.current = false; setBusy(false); }
  }

  // Abre o seletor de pasta nativo (repo.pickFolder) e fixa a pasta escolhida.
  // Reusado pelo botão "Pasta…" E pelo onChange do Scope — selecionar "Local" já
  // pergunta a pasta (pedido do Pedro), pra nunca cair silenciosamente no ~/Apolo.
  async function pickLocalFolder() {
    if (typeof window === 'undefined' || !window.apolo?.repo?.pickFolder) return;
    try {
      const r = await window.apolo.repo.pickFolder();
      const path = typeof r === 'string' ? r : r?.path;
      if (typeof path === 'string' && path.startsWith('/')) {
        setLocalDir(path);
        // pasta sem git não hospeda worktree → escreve direto
        if (r && r.isGit === false) setPref('mode', 'direct');
      }
    } catch {
      /* usuário cancelou / sem bridge — mantém a pasta atual */
    }
  }

  // Clicar no git/local do CABEÇALHO (slug do repo ou o caminho) abre o seletor
  // de pasta — garante scope local primeiro (pedido do Pedro: trocar o alvo ali
  // em cima, não só na pill de Scope).
  async function pickAnchorFolder() {
    if (ui.scope !== 'local') setPref('scope', 'local');
    await pickLocalFolder();
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim() && attachments.length === 0 && invokedSkills.length === 0) return;
    if (blocked) {
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'error', message: verdict.blockReason || 'Resolva o conflito de repositório antes de enviar.' },
      }));
      return;
    }
    if (verdict.needsConfirm) {
      setConfirmOpen(true);
      return;
    }
    void doSubmit();
  }

  useEffect(() => {
    function onInsert(e) {
      const insertText = e.detail?.text;
      if (typeof insertText !== 'string') return;
      setText((prev) => prev + insertText);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    window.addEventListener('apolo:insertComposer', onInsert);
    return () => window.removeEventListener('apolo:insertComposer', onInsert);
  }, []);

  useEffect(() => {
    const focus = () => requestAnimationFrame(() => textareaRef.current?.focus());
    window.addEventListener('apolo:focusCodeComposer', focus);
    return () => window.removeEventListener('apolo:focusCodeComposer', focus);
  }, []);

  const contextualPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;
    if (context?.slug) {
      const target = context.branch ? `${context.slug}/${context.branch}` : context.slug;
      return `Tarefa para ${target} — o que o agente deve fazer?`;
    }
    return 'O que vamos fazer agora? Enter envia, Shift+Enter pra nova linha…';
  }, [placeholder, context]);

  return (
    <form
      onSubmit={handleSubmit}
      aria-busy={busy}
      {...dropZoneHandlers}
      className={
        // Mesmo estilo do GitHubReposCard: glass-card bg + border + specular +
        // sombra externa. Foco mantém o accent border + ring (composam via os
        // CSS vars de shadow do Tailwind).
        // Estilo glass-card (igual GitHubReposCard). box-shadow (specular +
        // sombra externa) e ring de foco vivem no cursor-chrome.css, porque o
        // Tailwind interpreta shadow-[var(...)] como shadow-COLOR, não box-shadow.
        'composer-card relative flex w-full min-w-0 flex-col gap-2 rounded-xl border bg-[var(--apolo-glass-card-bg)] p-3 transition-all duration-150 ' +
        (dragOver
          ? 'border-apolo-accent bg-apolo-accentMuted/30'
          : 'border-[var(--apolo-glass-card-border)] focus-within:border-apolo-accent')
      }
    >
      <AttachmentDropOverlay visible={dragOver} />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        {...fileInputHandlers}
        className="hidden"
        aria-hidden="true"
      />

      {/* Âncora de alvo — fonte única (git vivo), no topo do card. Clicar no
          repo (git) ou no caminho (local) abre o seletor de pasta. */}
      <GitContextHeader
        context={context}
        mode={ui.mode}
        onSlugClick={pickAnchorFolder}
        onDirClick={pickAnchorFolder}
      />

      {/* Guarda pré-envio: conflito de repo bloqueia até resolver. */}
      {context?.health === 'conflict' && !conflictAck && (
        <GuardBanner
          context={context}
          onUseDetected={() => setPref('github_repo', null)}
          onUseSelected={() => setConflictAck(true)}
        />
      )}

      <div className="relative">
        <textarea
          ref={textareaRef}
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (text.trim()) handleSubmit(e);
            }
          }}
          placeholder={contextualPlaceholder}
          aria-label="Mensagem para o agente"
          className="w-full resize-y rounded-md border-0 bg-transparent text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
        />
        <div className="absolute left-0 top-full mt-1 z-30">
          <SlashCommandAutocomplete
            input={text}
            onPick={handlePickCommand}
            anchorRef={textareaRef}
            onDismiss={() => textareaRef.current?.focus()}
          />
        </div>
      </div>

      <InvokedSkillsBar
        skills={invokedSkills}
        onRemove={(slash) => setInvokedSkills((prev) => prev.filter((c) => c.slash_command !== slash))}
      />
      <AttachmentChips files={attachments} onRemove={removeAttachment} />
      {uploadError && (
        <p role="alert" className="text-[11px] text-apolo-error">
          {uploadError}
        </p>
      )}

      <ComposerSubmitError error={submitError} busy={busy} onNewDraft={() => {
        setClientIntentId(createClientIntentId()); setSubmitError(null);
        textareaRef.current?.focus();
      }} />
      {savedDrafts.length > 0 && <select aria-label="Recuperar rascunho" disabled={busy} className="rounded-lg bg-apolo-surface2 px-3 py-2 text-sm" value="" onChange={(e) => void restoreSaved(e.target.value)}>
        <option value="">Recuperar rascunho…</option>
        {savedDrafts.map((draft) => <option key={draft.client_intent_id} value={draft.client_intent_id}>{draft.text.slice(0, 60) || 'Rascunho com anexos ou skills'}</option>)}
      </select>}

      {/* Toolbar unificada. */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <SegmentedControl
          value={ui.mode}
          options={MODE_OPTIONS}
          onChange={(v) => setPref('mode', v)}
          ariaLabel="Modo de trabalho"
        />
        <PillDropdown
          Icon={Folder}
          label="Scope"
          value={ui.scope}
          options={SCOPE_OPTIONS}
          onChange={(v) => {
            setPref('scope', v);
            // Selecionar "Local" já abre o seletor de pasta (pedido do Pedro):
            // pergunta em qual pasta trabalhar em vez de assumir o ~/Apolo.
            if (v === 'local') void pickLocalFolder();
          }}
          ariaLabel="Escopo da sessão"
        />
        {ui.scope === 'github' && <RepoPickerPill />}
        {ui.scope === 'local' && typeof window !== 'undefined' && window.apolo?.repo?.pickFolder && (
          <button
            type="button"
            onClick={pickLocalFolder}
            aria-label={localDir ? `Pasta escolhida: ${localDir}. Clique para trocar.` : 'Escolher uma pasta local'}
            title={localDir || 'Escolher uma pasta local (qualquer projeto, git opcional)'}
            className="inline-flex max-w-[200px] items-center gap-1 truncate rounded-full border border-apolo-borderSubtle px-2 py-1 text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary"
          >
            <Folder size={11} aria-hidden="true" />
            <span className="truncate">{localDir ? (localDir.split('/').filter(Boolean).pop() || localDir) : 'Pasta…'}</span>
          </button>
        )}
        <PillDropdown
          Icon={GitBranch}
          label="Branch"
          value={ui.branch}
          options={BRANCH_OPTIONS}
          onChange={(v) => setPref('branch', v)}
          ariaLabel="Branch alvo"
        />

        <span className="mx-0.5 hidden h-4 w-px bg-apolo-borderSubtle sm:inline-block" aria-hidden="true" />

        <button
          type="button"
          onClick={() => setPref('permissions_mode', ui.permissions_mode === 'ask' ? 'ignore' : 'ask')}
          aria-label={`Modo de permissões atual: ${ui.permissions_mode === 'ask' ? 'perguntar' : 'auto-aprovar'}. Clique para alternar.`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 transition ${
            ui.permissions_mode === 'ask'
              ? 'border-apolo-borderSubtle text-apolo-textSecondary'
              : 'border-apolo-warning bg-apolo-warningBg text-apolo-warning'
          }`}
        >
          {ui.permissions_mode === 'ask' ? (
            <ShieldQuestion size={11} aria-hidden="true" />
          ) : (
            <ShieldCheck size={11} aria-hidden="true" />
          )}
          {ui.permissions_mode === 'ask' ? 'Perguntar' : 'Auto-aprovar'}
        </button>
        <TeamModeToggle active={!!ui.team_mode} onToggle={(next) => setPref('team_mode', next)} />
        <MicButton onTranscript={(delta) => setText((prev) => (prev ? prev + delta : delta))} />
        <button
          type="button"
          onClick={openFilePicker}
          title="Anexar arquivos (qualquer tipo)"
          aria-label="Anexar arquivos"
          className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-apolo-textSecondary transition hover:border-apolo-accent/40 hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
        >
          <Paperclip size={11} aria-hidden="true" />
          Anexar
        </button>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('apolo:openSkillPalette'))}
          title="Atalhos (⌘K)"
          aria-label="Abrir Command Palette de atalhos (⌘K)"
          className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
        >
          <Command size={11} aria-hidden="true" className="text-apolo-accent" />
          Atalhos
        </button>

        {/* Cluster direito (modelo · Skills · enviar) agrupado + justify-end:
            quebra como UMA unidade em telas estreitas, então o CTA "Criar
            sessão" nunca fica órfão sozinho numa linha com um vão enorme. */}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <label className="flex items-center gap-1">
            <span className="sr-only">Modelo</span>
            {modelKind.Icon ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] text-apolo-textTertiary"
                title={`Execução ${modelKind.label}`}
              >
                <modelKind.Icon size={11} aria-hidden="true" />
                <span>{modelKind.label}</span>
              </span>
            ) : null}
            <ModelDropdown value={ui.model} onChange={(id) => setPref('model', id)} groups={modelGroups} />
          </label>
          <button
            type="button"
            onClick={() => setShowSkillsModal(true)}
            aria-label="Templates de skills"
            title="Abrir templates de skills"
            className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2/60 px-2 py-1.5 text-sm font-medium text-apolo-textSecondary transition hover:border-violet-500/40 hover:bg-violet-500/10 focus:outline-none focus:ring-2 focus:ring-violet-500/40"
          >
            <Sparkles size={12} aria-hidden="true" className="text-violet-400" strokeWidth={2.25} />
            <span className="hidden sm:inline">Skills</span>
          </button>
          <button
            type="submit"
            disabled={(!text.trim() && attachments.length === 0 && invokedSkills.length === 0) || blocked || !draftReady || busy}
            title={blocked ? (verdict.blockReason || 'Resolva o conflito de repositório') : undefined}
            className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1.5 text-sm font-medium text-apolo-bg transition hover:bg-apolo-accent/90 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 disabled:opacity-50"
          >
            <Send size={12} aria-hidden="true" />
            {busy ? 'Criando chat…' : primaryAction}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        tone="warning"
        title={`Escrever direto na ${context?.branch || 'branch protegida'}?`}
        description={`Modo Direto grava na árvore de trabalho viva${context?.slug ? ` de ${context.slug}` : ''}. Numa branch protegida isso não é isolado e é difícil de desfazer.`}
        confirmLabel={`Escrever na ${context?.branch || 'branch'}`}
        cancelLabel="Usar Worktree"
        onConfirm={() => { setConfirmOpen(false); void doSubmit(); }}
        onCancel={() => { setConfirmOpen(false); setPref('mode', 'worktree'); }}
      />

      <SkillTemplatesModal
        open={showSkillsModal}
        onClose={() => {
          setShowSkillsModal(false);
          requestAnimationFrame(() => textareaRef.current?.focus());
        }}
      />
    </form>
  );
}
